import { getApiClient, getRawApiBaseUrl } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type {
  ApiErrorResponse,
  PublicArticleListQuery,
  ArticleSummary,
  PublicArticle,
  ServiceResponse,
} from '@dopomogai/types';

const BASE_URL = '/public/articles';

export interface PublicArticleRequestResult {
  article?: PublicArticle;
  redirectPath?: string | null;
  responseUrl?: string | null;
}

const normalizeApiBase = (value: string) => value.replace(/\/+$/, '');

const withApiV1 = (value: string) => {
  const normalized = normalizeApiBase(value);
  return normalized.endsWith('/api/v1') ? normalized : `${normalized}/api/v1`;
};

const buildPublicApiUrl = (pathname: string, query?: Record<string, string | number | undefined>) => {
  const rawBase = getRawApiBaseUrl();
  const normalizedBase = withApiV1(rawBase);
  const base = /^https?:\/\//i.test(normalizedBase)
    ? normalizedBase
    : new URL(normalizedBase, window.location.origin).toString().replace(/\/+$/, '');

  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const url = new URL(`${base}${path}`);

  for (const [key, value] of Object.entries(query || {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }

  return url.toString();
};

const buildFetchError = async (response: Response, operation: string): Promise<ApiErrorResponse> => {
  let message = `Request failed with status code ${response.status}`;
  let data: any = undefined;

  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await response.json();
      if (typeof data?.detail === 'string') {
        message = data.detail;
      } else if (typeof data?.message === 'string') {
        message = data.message;
      }
    } else {
      const text = await response.text();
      if (text && !text.trim().startsWith('<')) {
        message = text;
      } else {
        message = `Received an HTML error page (status ${response.status}) during ${operation}.`;
      }
      data = text;
    }
  } catch (error) {
    data = error;
  }

  return {
    isError: true,
    message,
    status: response.status,
    data,
  };
};

const fetchPublicArticle = async (
  url: string,
  operation: string,
): Promise<PublicArticleRequestResult | ApiErrorResponse> => {
  try {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    const finalUrl = response.url || url;
    const finalPath = new URL(finalUrl, window.location.origin).pathname;

    if (response.redirected && finalPath.startsWith('/blog/')) {
      return {
        redirectPath: `${finalPath}${new URL(finalUrl, window.location.origin).search}`,
        responseUrl: finalUrl,
      };
    }

    if (!response.ok) {
      return await buildFetchError(response, operation);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return {
        isError: true,
        status: response.status,
        message: `Received a non-JSON response during ${operation}.`,
        data: await response.text(),
      };
    }

    const article = (await response.json()) as PublicArticle;
    return {
      article,
      responseUrl: finalUrl,
    };
  } catch (error: any) {
    return {
      isError: true,
      message: error?.message || `Failed during ${operation}.`,
      status: null,
      originalError: error,
    };
  }
};

export const articlesService = {
  getList: (query: PublicArticleListQuery = {}): ServiceResponse<ArticleSummary[]> => {
    const params = {
      locale: query.locale || 'en',
      category: query.category,
      tag: query.tag,
      search: query.search,
      limit: query.limit,
    };

    return getApiClient()
      .get<ArticleSummary[]>(BASE_URL, { params })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'fetching article list'));
  },

  getBySlug: async (
    slug: string,
    locale = 'en',
  ): Promise<PublicArticleRequestResult | ApiErrorResponse> => {
    const url = buildPublicApiUrl(`${BASE_URL}/${encodeURIComponent(slug)}`, { locale });
    return fetchPublicArticle(url, `fetching article ${slug}`);
  },

  getByRoute: async (
    path: string,
    locale = 'en',
  ): Promise<PublicArticleRequestResult | ApiErrorResponse> => {
    const url = buildPublicApiUrl(`${BASE_URL}/by-route`, { path, locale });
    return fetchPublicArticle(url, `fetching article by route ${path}`);
  },

  getPreview: async (
    token: string,
  ): Promise<PublicArticleRequestResult | ApiErrorResponse> => {
    const url = buildPublicApiUrl(`${BASE_URL}/preview/${encodeURIComponent(token)}`);
    return fetchPublicArticle(url, 'fetching article preview');
  },
};
